import { useCallback, useEffect, useRef, useState } from 'react';

const DRAWER_WIDTH_STORAGE_KEY = 'accountDrawerWidth';
const MIN_DRAWER_WIDTH = 600;
const MAX_DRAWER_WIDTH_RATIO = 1;
const DEFAULT_DRAWER_WIDTH_RATIO = 0.5;

const clampDrawerWidth = (width: number) => {
  const maxWidth = window.innerWidth * MAX_DRAWER_WIDTH_RATIO;
  return Math.min(Math.max(width, MIN_DRAWER_WIDTH), maxWidth);
};

const getStoredDrawerWidth = () => {
  const stored = Number(localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY));
  const width =
    stored && Number.isFinite(stored)
      ? stored
      : window.innerWidth * DEFAULT_DRAWER_WIDTH_RATIO;
  return clampDrawerWidth(width);
};

/** Drag-to-resize behaviour for the account drawer panel, persisted across sessions. */
export function useDrawerResize() {
  const [drawerWidth, setDrawerWidth] = useState(getStoredDrawerWidth);
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const drawerWidthRef = useRef(drawerWidth);
  drawerWidthRef.current = drawerWidth;

  const handleDrawerResizeStart = useCallback(() => {
    setIsResizingDrawer(true);
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      setDrawerWidth((currentWidth) => clampDrawerWidth(currentWidth));
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (!isResizingDrawer) return;

    const handleMouseMove = (e: MouseEvent) => {
      const nextWidth = window.innerWidth - e.clientX;
      setDrawerWidth(clampDrawerWidth(nextWidth));
    };

    const handleMouseUp = () => {
      setIsResizingDrawer(false);
      localStorage.setItem(
        DRAWER_WIDTH_STORAGE_KEY,
        String(drawerWidthRef.current),
      );
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingDrawer]);

  return { drawerWidth, isResizingDrawer, handleDrawerResizeStart };
}
